import highlightWidget from 'components/Dashboard/apiCalls/callHighlightWidget';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import callHightlightContentAPI from 'components/Dashboard/utilities/callHightlightContentAPI';
import ChangeKeywordsExclusions from 'components/Dashboard/utilities/changeKeywordExclusions';
import { getBodyHighlightField, getTitleHighlightField } from './contentApiPaths';
import {
  applyAnswerHighlight,
  applyHighlightedPayload,
  applyQuestionAnswerHighlightedPayload,
  resolvePrimaryHighlightObjectType,
} from './highlightHelpers';

const useFetchSpecificContent = () => {
  const { generalApiCall } = useGeneralApiCall();

  const fetchSpecificContent = async ({
    pathname,
    source,
    type,
    contentType,
    urlType,
    topicId,
    highlight = true,
    includeBrandingProperty,
    needsAuthentication,
    returnCompleteRequest,
  }) => {
    const currentNew = await generalApiCall({
      method: 'get',
      pathname,
      requestSource: source,
      notShowErrorMessage: true,
      needsAuthentication,
      returnCompleteRequest,
    });

    if (!currentNew) {
      return null;
    }

    // /api/crm-interaction/{id} returns the raw ES-indexed CrmInteraction model, which has
    // neither an objectType field nor a lowercase objectId (only the PascalCase objectID) —
    // unlike the Content.* DTOs the other report-eligible types use.
    if (type === 'CrmInteraction') {
      currentNew.objectType = 'CrmInteraction';
      currentNew.objectId = currentNew.objectId ?? currentNew.objectID;
    }

    let newData = { ...currentNew };
    if (!highlight) {
      return { currentNew, newData };
    }

    const { objectId, objectType, questionHasAnswer } = currentNew;
    const objectTypeToApi = resolvePrimaryHighlightObjectType({ contentType, objectType });
    const highlightedData = await highlightWidget(objectTypeToApi, objectId, topicId, includeBrandingProperty);

    if (highlightedData) {
      newData = applyHighlightedPayload({
        content: newData,
        highlightedData,
        titleField: getTitleHighlightField({ type, contentType, urlType }),
        bodyField: getBodyHighlightField({ type, contentType, urlType, currentNew: newData }),
        titleFallbackField: 'title',
      });
    }

    if (questionHasAnswer) {
      const highlightedAnswer = await highlightWidget('WrittenAnswer', objectId, topicId);
      if (highlightedAnswer) {
        newData = applyAnswerHighlight({ content: newData, highlightedAnswer });
      }
    }

    return { currentNew, newData };
  };

  const highlightSpecificContentBySearch = async ({
    content,
    source,
    queryHighlight,
    andKeywords,
    forceHighlight,
    isHighlightActive,
    routeType,
    titleField,
    bodyField,
    titleFallbackField = 'title',
  }) => {
    let newData = { ...content };
    if (!isHighlightActive) {
      return newData;
    }

    const { objectId, granuleId, objectType: objectTypeProp, questionHasAnswer } = content ?? {};
    const createHighlightProperties = (highlightObjectType) => [
      objectId ?? granuleId,
      highlightObjectType ?? objectTypeProp ?? 'CREC',
      queryHighlight,
      ChangeKeywordsExclusions(andKeywords),
      source,
    ];

    const questionAndAnswerType = objectId?.includes('QuestionAndAnswer') || routeType?.includes('q-and-a');
    if (questionAndAnswerType) {
      const highlightedQuestion = await callHightlightContentAPI(createHighlightProperties('WrittenQuestion'));
      let highlightedAnswer;
      if (questionHasAnswer) {
        highlightedAnswer = await callHightlightContentAPI(createHighlightProperties('WrittenAnswer'));
      }
      newData = applyQuestionAnswerHighlightedPayload({
        content: newData,
        highlightedQuestion,
        highlightedAnswer,
        titleFallbackField,
        questionField: 'questionText',
        answerField: 'answerText',
      });
      return newData;
    }

    const highlightedData = await callHightlightContentAPI(createHighlightProperties(objectTypeProp));
    if (highlightedData) {
      newData = applyHighlightedPayload({
        content: newData,
        highlightedData,
        titleField,
        bodyField,
        titleFallbackField,
      });
    }
    return newData;
  };

  return { fetchSpecificContent, highlightSpecificContentBySearch };
};

export default useFetchSpecificContent;
